import React, { ReactNode } from "react";
import { Box, Divider } from "@mui/material";
import { ediphiPalette } from "../../styles/colors";
import { Card } from "./Card.styled";
import { HeaderText } from "../text/HeaderText.styled";

interface DetailCardProps {
  title: string;
  /** Coloured left bar, e.g. a project's own colour. A thin neutral border when absent. */
  accentColor?: string | null;
  onClick?: () => void;
  /**
   * Directly under the title and inside the same column, so it stays beside `action` rather than
   * wrapping below it — the identity lines of the user card.
   */
  subtitle?: ReactNode;
  /** Top-right slot, e.g. the user card's Log Out button. */
  action?: ReactNode;
  /** The body, below a divider. */
  children?: ReactNode;
  /** Below a second divider, e.g. the Use Ediphi Colors toggle. */
  footer?: ReactNode;
  /** Resting outline matching the drawer's choice rows. Off by default. */
  outlined?: boolean;
}

const ACCENT_WIDTH = "12px";
const RESTING_OUTLINE = `inset 0 0 0 1px ${ediphiPalette.borderColor}`;
const HOVER_OUTLINE = `inset 0 0 0 2px ${ediphiPalette.brand500}`;
const NO_OUTLINE = "inset 0 0 0 0 transparent";

/**
 * Outline first so it paints over the accent bar, and both slots always present so the resting and
 * hover lists line up — box-shadow interpolates by position, and a list that changes length or order
 * animates the accent into the outline, which reads as the bar sliding.
 */
const cardShadow = (outline: string, accentColor?: string | null) =>
  `${outline}, inset ${ACCENT_WIDTH} 0 0 0 ${accentColor ?? "transparent"}`;

/**
 * A titled card with a divider above its body. Built for read-mostly detail — a project, the signed-in
 * user — rather than the workbook items `WorkbookItemCard` covers, which carry their own actions.
 */
export const DetailCard: React.FC<DetailCardProps> = ({
  title,
  subtitle,
  accentColor,
  onClick,
  action,
  children,
  footer,
  outlined,
}) => (
  <Card
    onClick={onClick}
    sx={{
      flexDirection: "column",
      gap: 1,
      width: "100%",
      // Drawn as an inset shadow rather than a border so the bar does not shift the card's content box
      // relative to an unaccented sibling.
      boxShadow: cardShadow(outlined ? RESTING_OUTLINE : NO_OUTLINE, accentColor),
      ...(onClick && {
        cursor: "pointer",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: cardShadow(HOVER_OUTLINE, accentColor) },
      }),
      ...(accentColor && { pl: 3 }),
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {/* A step up from HeaderText's 14px: on these cards the title is the subject, not a section label. */}
        <HeaderText sx={{ fontSize: "16px" }}>{title}</HeaderText>
        {subtitle}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
    {/* toArray rather than a truthiness check: a caller's conditional rows arrive as an array of
        nulls, which is truthy, and would draw a divider over an empty body. */}
    {React.Children.toArray(children).length > 0 && (
      <>
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>{children}</Box>
      </>
    )}
    {footer && (
      <>
        <Divider />
        {footer}
      </>
    )}
  </Card>
);

export default DetailCard;
